import importD3ThreeD from '../utilities/D3ThreeD';

/**
 * A utility function which exposes methods for converting SVG shape elements to ThreeJS meshes.
 * 
 * @export {function} An initialization function to set up the utility
 * @param {any} importTHREE A reference to the imported THREE library
 * @returns {object} A class-like object with methods for the various shapes being converted
 */
export default function SVGToMesh( importTHREE ) {
    const THREE = importTHREE, exports = {}, D3ThreeD = importD3ThreeD( THREE );
    
    // TODO: Check if TriangleStripDrawMode is preferable for rendering optimization
    // https://threejs.org/docs/#api/constants/DrawModes
    // mesh.drawMode = THREE.TriangleStripDrawMode;

    /**
     * Converts an SVG <rect> element to a THREE mesh
     * 
     * @param {any} rectSVG SVG <rect> element
     * @param {string} [fillColor="#000000"] Optional fill color
     * @param {any} edgeColor Optional edge color
     * @returns THREE.mesh of the <rect>
     */
    function rect (rectSVG, fillColor = "#000000", edgeColor) {
        const rectShape = new THREE.Shape(),
              x = parseFloat( rectSVG.getAttribute("x")),
              y = parseFloat( rectSVG.getAttribute("y")),
              width = parseFloat( rectSVG.getAttribute("width")),
              height = parseFloat( rectSVG.getAttribute("height")),
              transform = rectSVG.getAttribute("transform");

        // Create shape of rect
        rectShape.moveTo( 0, 0 );
        rectShape.lineTo( width, 0);
        rectShape.lineTo( width, height);
        rectShape.lineTo( 0, height);
        rectShape.lineTo( 0, 0);

        const extrudeSettings = { amount: -15, bevelEnabled: false };

        const geometry = new THREE.ExtrudeGeometry( rectShape, extrudeSettings );

        const mesh = new THREE.Mesh( geometry, new THREE.MeshBasicMaterial({color: fillColor}) );
        mesh.name = rectSVG.getAttribute('id');

        // TODO: Support z position?
        mesh.position.set(x, y, 0);

        // Handle transform application if it exists
        if (transform) {
            // Determine rotation in radians
            const values = transform.split('(')[1].split(')')[0].split(' '),
                  a = values[0],
                  b = values[1],
                  radians = Math.atan2(b, a);

            // Rotate the mesh around the "corner" position
            mesh.rotateAroundWorldAxis( new THREE.Vector3(x + (width / 2), y + (height / 2), 0), new THREE.Vector3(0, 0, 1), radians );
        }
        
        // Add edges if a color is provided
        if (edgeColor !== null && edgeColor !== "" && edgeColor !== "none" && edgeColor !== undefined) {
            var geo = new THREE.EdgesGeometry( mesh.geometry );
            var mat = new THREE.LineBasicMaterial( { color: edgeColor, linewidth: 1 } );
            var wireframe = new THREE.LineSegments( geo, mat );
            mesh.add( wireframe );
        }

        return mesh;
    };
    exports.rect = rect;

    /**
     * Converts an SVG <circle> element to a THREE mesh
     * 
     * @param {any} circleSVG SVG <circle> element to convert
     * @param {string} [fillColor="#000000"] Optional fill color
     * @param {any} edgeColor Optional edge color
     * @returns THREE.mesh of the <circle>
     */
    function circle (circleSVG, fillColor = "#000000", edgeColor) {
        const circleShape = new THREE.Shape(),
              circleRadius = parseFloat( circleSVG.getAttribute("r")),
              x = parseFloat( circleSVG.getAttribute("cx")),
              y = parseFloat( circleSVG.getAttribute("cy"));

        circleShape.moveTo( 0, 0 );
        circleShape.ellipse( 0, 0, circleRadius, circleRadius, 0, 2 * Math.PI);

        const extrudeSettings = { amount:-10, bevelEnabled: false };

        const geometry = new THREE.ExtrudeGeometry( circleShape, extrudeSettings );

        const mesh = new THREE.Mesh( geometry, new THREE.MeshBasicMaterial({color: fillColor}) );
        mesh.name = circleSVG.getAttribute('id');

        // TODO: Support z position?
        mesh.position.set(x, y, 0);

        // Add edges if a color is provided
        if (edgeColor !== null && edgeColor !== "" && edgeColor !== "none" && edgeColor !== undefined) {
            var geo = new THREE.EdgesGeometry( mesh.geometry );
            var mat = new THREE.LineBasicMaterial( { color: edgeColor, linewidth: 1 } );
            var wireframe = new THREE.LineSegments( geo, mat );
            mesh.add( wireframe );
        }

        return mesh;
    };
    exports.circle = circle;

    /**
     * Converts an SVG <polygon> element to a THREE mesh
     * 
     * @param {any} polygonSVG SVG <polygon> element to convert
     * @param {string} [fillColor="#000000"] Optional fill color
     * @param {any} edgeColor Optional edge color
     * @returns THREE.mesh of the <polygon>
     */
    function polygon (polygonSVG, fillColor = "#000000", edgeColor) {
        const points = polygonSVG.getAttribute("points");

        // Construct shape from polygon point atttributes
        const polygonShape = new THREE.Shape(
            points.split(" ")
                .filter((pointString) => pointString !== "")
                .map((pointString) => {
                    const points = pointString.split(",");
                    return new THREE.Vector2(points[0], points[1]); 
                })
        );

        const extrudeSettings = { amount: 0, bevelEnabled: false };

        const geometry = new THREE.ExtrudeGeometry( polygonShape, extrudeSettings );

        const mesh = new THREE.Mesh( geometry, new THREE.MeshBasicMaterial({color: fillColor}) );
        mesh.name = polygonSVG.getAttribute('id');
        
        // Add edges if a color is provided
        if (edgeColor !== null && edgeColor !== "" && edgeColor !== "none" && edgeColor !== undefined) {
            var geo = new THREE.EdgesGeometry( mesh.geometry );
            var mat = new THREE.LineBasicMaterial( { color: edgeColor, linewidth: 1 } );
            var wireframe = new THREE.LineSegments( geo, mat );
            mesh.add( wireframe );
        }

        return mesh;
    };
    exports.polygon = polygon;

    /**
     * Converts an SVG <circle> element to a THREE mesh
     * 
     * @param {any} circleSVG SVG <circle> element to convert
     * @param {string} [fillColor="#000000"] Optional fill color
     * @param {any} edgeColor Optional edge color
     * @returns THREE.mesh of the <circle>
     */
    // Uses concepts from https://github.com/josdirksen/learning-threejs/blob/7469a6010b0a19949d1c3ea402a604d28f6a1905/chapter-06/05-extrude-svg.html
    exports.path = function path (pathSVG, fillColor = "#000000", edgeColor) {
        const pathString = pathSVG.getAttribute("d");

        var shape = D3ThreeD.transformSVGPathExposed(pathString);

        const extrudeSettings = { amount: 0, bevelEnabled: false };

        const geometry = new THREE.ExtrudeGeometry( shape, extrudeSettings );

        const mesh = new THREE.Mesh( geometry, new THREE.MeshBasicMaterial({color: fillColor}) );
        mesh.name = pathSVG.getAttribute('id');
        
        // Add edges if a color is provided
        if (edgeColor !== null && edgeColor !== "" && edgeColor !== "none" && edgeColor !== undefined) {
            var geo = new THREE.EdgesGeometry( mesh.geometry );
            var mat = new THREE.LineBasicMaterial( { color: edgeColor, linewidth: 1 } );
            var wireframe = new THREE.LineSegments( geo, mat );
            mesh.add( wireframe );
        }

        return mesh;
    };

    
    /**
     * Adds a prototype method to THREE.Object3D to rotate the object around an axis in world space
     * From https://stackoverflow.com/questions/31953608/rotate-object-on-specific-axis-anywhere-in-three-js-including-outside-of-mesh/32038265#32038265
     *
     * @returns Object3D for chaining
     */
    function rotateAroundWorldAxis () {
        // rotate object around axis in world space (the axis passes through point)
        // axis is assumed to be normalized
        // assumes object does not have a rotated parent
    
        var q = new THREE.Quaternion();
    
        return function rotateAroundWorldAxis( point, axis, angle ) {
    
            q.setFromAxisAngle( axis, angle );
    
            this.applyQuaternion( q );
    
            this.position.sub( point );
            this.position.applyQuaternion( q );
            this.position.add( point );
    
            return this;
    
        }
    };
    THREE.Object3D.prototype.rotateAroundWorldAxis = rotateAroundWorldAxis();

    return exports;
}



// WEBPACK FOOTER //
// ./src/utilities/SVGToMesh.js